import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Alert from '@spectrum-icons/workflow/Alert';
import { ToastQueue } from '@adobe/react-spectrum';
import styles from './CloneMomentModal.module.scss';
import { getCookie } from '../../../utils/helpers';
import {
  handle401Error,
  handle403Error
} from '../../../utils/authErrorHandler';

interface CloneMomentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClone?: (clonedName: string) => void;
  currentMomentName: string;
  issueId?: string; // Add issueId prop
  onCloneSuccess?: (clonedIssueId: string) => void; // Callback with new issueId
  /** RSD type for audit payload (same as slideout header) */
  momentType?: string;
}

// Get API base URL
const getApiBaseUrl = (): string => {
  return process.env.REACT_APP_API_V2_URL || '/product-roadmap/api/v2';
};

// Get authentication headers
const getAuthHeaders = () => {
  const token = getCookie('app_access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

export const CloneMomentModal: React.FC<CloneMomentModalProps> = ({
  isOpen,
  onClose,
  onClone,
  currentMomentName,
  issueId,
  onCloneSuccess,
  momentType
}) => {
  const [cloneName, setCloneName] = useState('');
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  );
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create portal container when modal should open
  useEffect(() => {
    if (isOpen) {
      const container = document.createElement('div');
      container.setAttribute('style', 'position: fixed; top: 0; left: 0;');
      document.body.appendChild(container);
      setPortalContainer(container);

      return () => {
        document.body.removeChild(container);
        setPortalContainer(null);
      };
    }
  }, [isOpen]);

  // Set default name when modal opens
  useEffect(() => {
    if (isOpen && currentMomentName) {
      setCloneName(`${currentMomentName}-Copy`);
    }
  }, [isOpen, currentMomentName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCloneName(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    // Validate that name is not empty
    if (!cloneName || cloneName.trim() === '') {
      return;
    }

    // Validate issueId
    if (!issueId) {
      ToastQueue.negative('No moment selected for cloning', {
        timeout: 5000
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Call clone API
      const apiBaseUrl = getApiBaseUrl();
      const headers = getAuthHeaders();

      const response = await fetch(`${apiBaseUrl}/moments/${issueId}/clone`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: cloneName.trim()
        })
      });

      // Handle authentication errors
      if (response.status === 401) {
        handle401Error('Cloning moment');
        throw new Error('Authentication required');
      }

      if (response.status === 403) {
        handle403Error('Cloning moment');
        throw new Error('Permission denied');
      }

      if (!response.ok) {
        let errorMessage = `Failed to clone moment: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use the default message
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      // Extract the new issueId from response
      const clonedIssueId = result.issueId || result.id;

      // Show success toast
      ToastQueue.positive('Moment cloned successfully', {
        timeout: 3000
      });

      // Call onClone callback
      if (onClone) {
        onClone(cloneName.trim());
      }

      // Call onCloneSuccess with the new issueId to open slideout
      if (onCloneSuccess && clonedIssueId) {
        onCloneSuccess(clonedIssueId);
      }

      // Reset and close
      handleCancel();
    } catch (error: any) {
      console.error('❌ Error cloning moment:', error);

      // Show error toast
      const errorMessage =
        error?.message || 'Failed to clone moment. Please try again.';
      ToastQueue.negative(errorMessage, {
        timeout: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setCloneName('');
    setHasSubmitted(false);
    setIsSubmitting(false);
    onClose();
  };

  // Field-level invalid flag
  const isNameInvalid = hasSubmitted && (!cloneName || cloneName.trim() === '');

  // Don't render anything if not open or no container
  if (!isOpen || !portalContainer) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={styles.modalOverlay}
      data-testid="clone-moment-modal-overlay"
    >
      <div
        className={styles.clonePopup}
        role="dialog"
        aria-modal="true"
        data-testid="clone-moment-popup"
        onClick={e => e.stopPropagation()}
      >
        <h2 className={styles.clonePopupTitle} data-testid="clone-moment-title">
          Clone Moment
        </h2>

        <form onSubmit={handleSubmit} data-testid="clone-moment-form">
          <div
            className={styles.formSection}
            data-testid="clone-moment-name-section"
          >
            <label
              htmlFor="cloneName"
              className={`${styles.fieldLabel} ${styles.required}`}
              data-testid="clone-moment-name-label"
            >
              NAME
            </label>
            <div className={styles.inputWrapper}>
              <input
                type="text"
                id="cloneName"
                name="cloneName"
                className={`${styles.textField} ${
                  isNameInvalid ? styles.error : ''
                } ${isNameInvalid ? styles.withIcon : ''}`}
                value={cloneName}
                onChange={handleInputChange}
                placeholder="Enter moment name"
                data-testid="clone-moment-name-input"
                autoFocus
                disabled={isSubmitting}
              />
              {isNameInvalid && (
                <div className={styles.iconRight}>
                  <Alert
                    UNSAFE_className={styles.errorIcon}
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
            {isNameInvalid && (
              <div
                className={styles.errorMessage}
                data-testid="clone-moment-name-error"
              >
                Required Field
              </div>
            )}
          </div>

          <div
            className={styles.buttonContainer}
            data-testid="clone-moment-button-container"
          >
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={handleCancel}
              disabled={isSubmitting}
              data-testid="clone-moment-cancel-button"
            >
              Cancel
            </button>

            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={isSubmitting}
              data-testid="clone-moment-clone-button"
            >
              {isSubmitting ? 'Cloning...' : 'Clone'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    portalContainer
  );
};
